/**
 * Auctions API client — مزادات السيارات
 * Follows the same pattern as marketplace.ts / rentals.ts.
 */
import apiClient from './client';

// ── Types ────────────────────────────────────────────────────────────────────

export interface AuctionImage {
  id: number;
  image: string | null;
  image_url: string | null;
  image_full_url: string | null;
  is_primary: boolean;
  sort_order: number;
}

export interface AuctionBid {
  id: number;
  bidder: number;
  bidder_name: string;
  amount: string;
  status: 'valid' | 'outbid' | 'cancelled' | 'winning';
  created_at: string;
}

export type AuctionStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'live'
  | 'ended'
  | 'cancelled'
  | 'sold';

export interface Auction {
  id: number;
  title: string;
  description?: string;
  brand: string;
  car_model: string;
  year: number;
  condition: 'new' | 'used' | 'salvage';
  color?: string;
  mileage?: number;
  fuel_type?: string;
  transmission?: string;
  country_code: string;
  location: string;
  starting_price: string;
  current_price: string;
  minimum_increment: string;
  reserve_price?: string;
  start_datetime: string;
  end_datetime: string;
  status: AuctionStatus;
  is_featured: boolean;
  seller_name: string;
  total_bids: number;
  time_remaining_seconds: number;
  primary_image_url: string | null;
  images?: AuctionImage[];
  recent_bids?: AuctionBid[];
  is_watched: boolean;
  is_seller?: boolean;
  is_highest_bidder?: boolean;
  winner?: number | null;
  winner_name?: string | null;
  admin_notes?: string;
  created_at: string;
}

export interface AuctionFilters {
  status?: AuctionStatus;
  brand?: string;
  country_code?: string;
  condition?: string;
  year?: number;
  is_featured?: boolean;
  price_min?: number;
  price_max?: number;
  ending_soon?: boolean;
  search?: string;
  ordering?: string;
  page?: number;
}

export interface AuctionStats {
  total: number;
  live: number;
  pending_approval: number;
  ended: number;
  sold: number;
  draft: number;
  cancelled: number;
  total_bids: number;
}

export interface AuctionSetting {
  id: number;
  default_minimum_increment: string;
  commission_percent: string;
  anti_snipe_extension_minutes: number;
  allow_auto_bid: boolean;
  require_admin_approval: boolean;
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Helper: extracts data from either a raw AxiosResponse or an already-unwrapped interceptor response */
function extract<T>(res: any): T {
  // Interceptor returns raw data object when response.data.status is present
  // (e.g. auction with status:'live' triggers early return in the interceptor)
  // For paginated list responses, they don't have .status so come as AxiosResponse
  if (res && res.data !== undefined) return res.data as T;
  return res as T;
}

export const auctionsApi = {
  /** List active/ended auctions (public) */
  list: async (filters?: AuctionFilters) => {
    const res = await apiClient.get('/auctions/', { params: filters });
    return extract<{ results: Auction[]; count: number } | Auction[]>(res);
  },

  /** Get a single auction by ID */
  get: async (id: number | string) => {
    const res = await apiClient.get(`/auctions/${id}/`);
    return extract<Auction>(res);
  },

  /** Place a bid on an auction (authenticated) */
  placeBid: async (id: number | string, amount: number) => {
    const res = await apiClient.post(`/auctions/${id}/bid/`, { amount });
    return extract<AuctionBid>(res);
  },

  /** Get bid history for an auction */
  getBids: async (id: number | string) => {
    const res = await apiClient.get(`/auctions/${id}/bids/`);
    return extract<AuctionBid[]>(res);
  },

  /** Add to watchlist */
  watch: async (id: number | string) => {
    const res = await apiClient.post(`/auctions/${id}/watch/`);
    return extract<{ watching: boolean }>(res);
  },

  /** Remove from watchlist */
  unwatch: async (id: number | string) => {
    const res = await apiClient.delete(`/auctions/${id}/watch/`);
    return extract<{ watching: boolean }>(res);
  },
};

// ── Admin API ─────────────────────────────────────────────────────────────────

export const auctionsAdminApi = {
  /** List all auctions (admin) */
  list: async (filters?: AuctionFilters) => {
    const { data } = await apiClient.get('/auctions/admin/auctions/', { params: filters });
    return data as { results: Auction[]; count: number } | Auction[];
  },

  /** Create a new auction */
  create: async (payload: Partial<Auction>) => {
    const { data } = await apiClient.post('/auctions/admin/auctions/', payload);
    return data as Auction;
  },

  /** Update an existing auction */
  update: async (id: number | string, payload: Partial<Auction>) => {
    const { data } = await apiClient.patch(`/auctions/admin/auctions/${id}/`, payload);
    return data as Auction;
  },

  /** Delete an auction */
  delete: async (id: number | string) => {
    await apiClient.delete(`/auctions/admin/auctions/${id}/`);
  },

  /** Approve an auction */
  approve: async (id: number | string) => {
    const { data } = await apiClient.post(`/auctions/admin/auctions/${id}/approve/`);
    return data;
  },

  /** Cancel an auction */
  cancel: async (id: number | string, reason?: string) => {
    const { data } = await apiClient.post(`/auctions/admin/auctions/${id}/cancel/`, { reason });
    return data;
  },

  /** Force-end an auction and set winner */
  end: async (id: number | string) => {
    const { data } = await apiClient.post(`/auctions/admin/auctions/${id}/end/`);
    return data as Auction;
  },

  /** Get all bids for an auction */
  getBids: async (id: number | string) => {
    const { data } = await apiClient.get(`/auctions/admin/auctions/${id}/bids/`);
    return data as AuctionBid[];
  },

  /** Get auction KPI stats */
  getStats: async () => {
    const { data } = await apiClient.get('/auctions/admin/auctions/stats/');
    return data as AuctionStats;
  },

  /** Get auction settings */
  getSettings: async () => {
    const { data } = await apiClient.get('/auctions/admin/auction-settings/');
    return data as AuctionSetting;
  },

  /** Update auction settings */
  updateSettings: async (payload: Partial<AuctionSetting>) => {
    const s = await auctionsAdminApi.getSettings();
    const { data } = await apiClient.patch(`/auctions/admin/auction-settings/${s.id}/`, payload);
    return data as AuctionSetting;
  },
};
